import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@saleshandy/design-system';
import { ChevronDown, ChevronUp, Pencil } from '@saleshandy/icons';

import { useSelector } from 'react-redux';
import { ViewType } from '../../../components/prospect/enums/prospect';
import { KanbanProspectListPayload } from '../../../components/prospect/types';
import { RootState } from '../../../store/root-reducer';
import { Images } from '../../app-constants';
import { Placement } from '../../design-system/components/overlay';
import Transfer from '../../design-system/components/transfer';
import { Plus } from '../../svg/plus';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import TruncatedTextWithTooltip from '../truncated-text-with-tooltip';
import {
  CategoryDropdownProps,
  CategoryGroup,
  CategoryGroupOption,
  CategoryItem,
  CategoryWithGroup,
} from './type';
import TruncatedTextWithAutoTooltip from '../truncated-text-with-auto-tooltip';
import { defaultKanbanPageSize } from '../../../components/prospect/components/prospect-kanban/utils/helper';

const CategoriesDropdown: React.FC<CategoryDropdownProps> = ({
  systemCategories = [],
  customCategories = [],
  onSelectionChange,
  onAddNewCategory,
  onEditCategory,
  selectLabel = 'Select category',
  btnClassName = '',
  disabled = false,
  customSelectBox = null,
  customCategoryClassName = '',
  show,
  setShow,
  fetchProspectField,
  filters,
  sendGetProspectListRequest,
  sendGetContactListByViewIdRequest,
  sendGetProspectListCountRequest,
  defaultCategoryIdForActiveView = null,
  setActiveCategoryName,
  contactField,
  setProspectViewIdFilter,
}) => {
  // ** Selectors
  const activeViewId: number | null = useSelector(
    (state: RootState) => state.prospect?.activeViewId,
  );

  // ** State Management
  const [search, setSearch] = useState<string>('');
  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState<CategoryWithGroup | null>(null);
  const [customFieldsLabel, setCustomFieldsLabel] = useState<
    Record<number, string>
  >({});
  const [customCategoriesData, setCustomCategoriesData] = useState<
    KanbanProspectListPayload[]
  >([]);

  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // ** Handlers
  const getCategoryLabel = useCallback(
    (item: KanbanProspectListPayload, group: CategoryGroup) => {
      if (group === 'custom' && item.categoryFieldId) {
        const customLabel = customFieldsLabel[item.categoryFieldId];
        if (customLabel) {
          return customLabel;
        }
      }

      if (group === 'system' && item?.metadata?.entityType === 'Call Outcome') {
        return 'Call Task Outcome';
      }

      return item.metadata?.entityType ?? item.name;
    },
    [customFieldsLabel],
  );

  const findCategoryWithGroup = useCallback(
    (
      categoryId: CategoryItem['id'],
      preferredGroup?: CategoryGroup,
    ): CategoryWithGroup | null => {
      const byGroup = (group: CategoryGroup) => {
        const source = group === 'system' ? systemCategories : customCategories;
        const match = source.find((category) => category.id === categoryId);
        return match ? { ...match, group } : null;
      };

      if (preferredGroup) {
        const preferredMatch = byGroup(preferredGroup);
        if (preferredMatch) {
          return preferredMatch;
        }
      }

      return byGroup('custom') ?? byGroup('system');
    },
    [customCategories, systemCategories],
  );

  const toggleCategory = useCallback(
    (categoryId: CategoryItem['id'], group: CategoryGroup) => {
      const category = findCategoryWithGroup(categoryId, group);
      if (category) {
        setSelectedCategory(category);
        onSelectionChange?.(Number(category?.id));
        setProspectViewIdFilter?.(null);

        if (!activeViewId) {
          sendGetProspectListRequest({
            pageNum: 1,
            pageSize: defaultKanbanPageSize,
            viewType: ViewType.Kanban,
            kanbanViewId: category?.id as number,
            filterGroupLogic: filters?.filterGroupLogic,
            search: filters?.search,
            sortBy:
              // eslint-disable-next-line no-restricted-globals
              filters?.sortBy != null && !isNaN(Number(filters?.sortBy))
                ? Number(filters?.sortBy)
                : filters?.sortBy,
            sortOrder: filters?.sortOrder,
          });
        } else {
          sendGetContactListByViewIdRequest({
            pageNum: 1,
            pageSize: defaultKanbanPageSize,
            viewType: ViewType.Kanban,
            viewId: activeViewId,
            kanbanViewId: category?.id as number,
          });
        }
        sendGetProspectListCountRequest({
          viewType: ViewType.List,
          viewId: activeViewId,
          kanbanViewId: category?.id as number,
          search: filters?.search,
        });
      }

      setShow(false);
    },
    [
      activeViewId,
      filters?.filterGroupLogic,
      filters?.search,
      filters?.sortBy,
      filters?.sortOrder,
      sendGetProspectListRequest,
      sendGetContactListByViewIdRequest,
      sendGetProspectListCountRequest,
      onSelectionChange,
      findCategoryWithGroup,
      setShow,
    ],
  );

  // ** Memoized Values
  // TODO: Remove console.log & this logic to set the name, it can be written at set time
  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) {
      return '';
    }
    const categories = selectedCategory as KanbanProspectListPayload;
    setActiveCategoryName(categories);
    if ('metadata' in selectedCategory) {
      return getCategoryLabel(
        selectedCategory as KanbanProspectListPayload,
        selectedCategory?.group,
      );
    }

    return selectedCategory?.name;
  }, [
    selectedCategory?.group,
    selectedCategory?.name,
    selectedCategory?.id,
    getCategoryLabel,
    setActiveCategoryName,
  ]);

  const triggerLabel = useMemo(() => selectedCategoryName || selectLabel, [
    selectedCategoryName,
    selectLabel,
  ]);

  // ** Effects
  useEffect(() => {
    if (!fetchProspectField?.length) {
      setCustomFieldsLabel({});
      return;
    }

    const labels = {
      ...(fetchProspectField?.reduce<Record<number, string>>((acc, field) => {
        if (field?.id) acc[field.id] = field?.label;
        return acc;
      }, {}) ?? {}),

      ...(contactField?.reduce<Record<number, string>>((acc, field) => {
        if (field?.id) acc[field.id] = field?.label;
        return acc;
      }, {}) ?? {}),
    };
    setCustomFieldsLabel(labels);
  }, [fetchProspectField, contactField]);

  useEffect(() => {
    if (!show) return;

    function handleClickOutside(e: MouseEvent) {
      if ((e.target as HTMLElement)?.closest('.category-dropdown')) {
        return;
      }
      // Ignore clicks inside Transfer overlay dropdowns except for the category dropdown value
      if (
        (e.target as HTMLElement)?.closest('.transfer-overlay') &&
        !(e.target as HTMLElement)?.closest('.category-dropdown-group ')
      ) {
        return;
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setShow(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  // ** Populate customCategoriesData from customCategories and their labels
  useEffect(() => {
    if (!customCategories?.length) {
      setCustomCategoriesData([]);
      return;
    }

    const categoriesWithLabels = customCategories?.map((category) => {
      // If category has a categoryFieldId, get its label
      if (
        category?.categoryFieldId &&
        customFieldsLabel[category?.categoryFieldId]
      ) {
        return {
          ...category,
          metadata: {
            ...category.metadata,
            entityType: customFieldsLabel[category?.categoryFieldId],
          },
        };
      }
      return category;
    });

    setCustomCategoriesData(categoriesWithLabels);
  }, [customCategories, customFieldsLabel]);

  useEffect(() => {
    const totalCategories = [
      ...(systemCategories || []),
      ...(customCategories || []),
    ];
    if (totalCategories?.length > 0) {
      const defaultCategory =
        totalCategories.find(
          (category) => category.id === defaultCategoryIdForActiveView,
        ) || systemCategories?.[0];
      setSelectedCategory({
        ...defaultCategory,
        group: defaultCategory?.isDefault ? 'system' : 'custom',
        name: '',
      });

      onSelectionChange?.(defaultCategory?.id);
    }
  }, [
    systemCategories,
    customCategories,
    defaultCategoryIdForActiveView,
    onSelectionChange,
  ]);

  const filteredGroupOptions = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    const filterItems = (items: KanbanProspectListPayload[]) => {
      if (!searchText) {
        return items;
      }

      return items?.filter((item) =>
        item?.metadata?.entityType?.trim()?.toLowerCase()?.includes(searchText),
      );
    };

    const baseOptions: CategoryGroupOption[] = [
      {
        key: 'system',
        label: 'System',
        group: 'system',
        items: filterItems(systemCategories),
      },
      {
        key: 'custom',
        label: 'Custom',
        group: 'custom',
        items: filterItems(customCategoriesData),
      },
    ];

    return baseOptions.filter((option) => option.items.length > 0);
  }, [systemCategories, customCategories, search, customCategoriesData]);

  const totalFilteredItems = useMemo(
    () =>
      filteredGroupOptions.reduce(
        (count, option) => count + option.items.length,
        0,
      ),
    [filteredGroupOptions],
  );

  const showEmptyState = useMemo(() => totalFilteredItems === 0, [
    totalFilteredItems,
  ]);

  const optionRenderer = useCallback(
    (option: CategoryGroupOption) => (
      <div className={`category-dropdown-group ${customCategoryClassName}`}>
        <div className="category-dropdown-group-header">{option.label}</div>
        <div className="category-dropdown-group-items">
          {option?.items?.map((item) => {
            if (!item) {
              return null;
            }
            const isChecked =
              !!selectedCategory &&
              selectedCategory?.group === option?.group &&
              selectedCategory?.id === item?.id;
            return (
              <div
                key={`${option?.group}-${item?.id}`}
                className={`category-dropdown-item ${
                  isChecked ? 'category-dropdown-item--selected' : ''
                }`}
                {...accessibleOnClick((event) => {
                  event?.stopPropagation?.();
                  if (item?.id === selectedCategory?.id) {
                    setShow(false);
                    return;
                  }
                  toggleCategory(item?.id, option?.group);
                })}
              >
                <span className="category-dropdown-item-label">
                  <TruncatedTextWithAutoTooltip
                    text={getCategoryLabel(item, option.group)}
                  />
                </span>
                {option.group === 'custom' && onEditCategory ? (
                  <div
                    className="category-dropdown-edit-btn"
                    {...accessibleOnClick((e) => {
                      e.stopPropagation();
                      onEditCategory(item, { group: option.group });
                    })}
                  >
                    <button
                      type="button"
                      className="category-dropdown-edit-icon"
                      aria-label={`Edit ${item?.name}`}
                    >
                      <Pencil />
                    </button>
                    <span className="category-dropdown-edit-text">Edit</span>
                  </div>
                ) : null}
              </div>
            );
          })}
          {option.items.length === 0 && !showEmptyState && (
            <div className="category-dropdown-empty">
              {search
                ? 'No categories match your search'
                : `No ${
                    option.group === 'system' ? 'system' : 'custom'
                  } categories`}
            </div>
          )}
        </div>
      </div>
    ),
    [
      customCategoryClassName,
      selectedCategory,
      onEditCategory,
      getCategoryLabel,
      toggleCategory,
    ],
  );

  return (
    <div ref={categoryDropdownRef}>
      <Transfer<CategoryGroupOption>
        show={show}
        placement={Placement.BottomStart}
        showSearch
        className="category-dropdown"
        filterOption={() => true}
        options={showEmptyState ? [] : filteredGroupOptions}
        customEmptyElement={
          <>
            <img src={Images.NoResult} alt="No Result" />
            <h1>No Result Found</h1>
            <p>Please try searching with another term</p>
          </>
        }
        showEmpty={showEmptyState}
        optionRenderer={optionRenderer}
        onSearch={(value: string) => {
          setSearch(value);
        }}
        footer={
          <Button
            variant="tertiary"
            className="category-dropdown-add-btn"
            onClick={() => {
              onAddNewCategory?.();
            }}
          >
            <Plus />
            Add New Category
          </Button>
        }
        multiSelect={false}
        clickListenerOn="row"
        showClearIcon={true}
      >
        <Button
          className={`transfer-select-btn category-dropdown-trigger ${btnClassName} ${
            disabled ? 'disabled' : ''
          }`}
          variant="tertiary"
          onClick={() => {
            if (!disabled) {
              setShow(!show);
              setSearch('');
            }
          }}
          ref={triggerRef}
        >
          <div className="transfer-select-btn-content">
            {customSelectBox || (
              <TruncatedTextWithTooltip string={triggerLabel} length={18} />
            )}
          </div>
          <div className="bs-select-box-icon-wrapper">
            {show ? <ChevronUp /> : <ChevronDown />}
          </div>
        </Button>
      </Transfer>
    </div>
  );
};

export default memo(CategoriesDropdown);
